import { ActionCreator, AjaxDispatcher } from './ajax-dispatcher';
import './analytics';
import Cart from './cart';
import { CartFileUploader } from './cart-file-uploader';
import { CartLoginForm } from './cart-login-form';
import CheckboxValueFilter from './catalog-filter/checkbox-value-filter';
import CheckboxFilter from './catalog-filter/checkbox/checkbox-filter';
import CheckboxFlatFilter from './catalog-filter/checkbox-flat-filter';
import ColorFilter from './catalog-filter/color/color-filter';
import FilterBase from './catalog-filter/filter-base';
import FilterBox from './catalog-filter/filter-box';
import RadioItemsFilter from './catalog-filter/radio-items-filter';
import RangeFilter from './catalog-filter/range-filter';
import SelectFilter from './catalog-filter/select-filter';
import TextFilter from './catalog-filter/text-filter';
import TextileSizeFilter from "./catalog-filter/textile-size/textile-size-filter";
import CategoryMenu from './category-menu';
import ChangePasswordForm from "./change-password-form";
import Collapsable from "./collapsable";
import { getAllowedCookies, getCookie, getPopup } from "./common";
import CompanyAddress from './company-address';
import CompanyContact from './company-contact';
import CompanyEdit from "./company-edit";
import CompanyPerson from "./company-person";
import { ContactForm } from './contact-form';
import { CookieConsent } from './cookie-consent';
import CurrencySelector from './currency-selector';
import { CompanyProfile } from "./customer-center/company-profile";
import { PersonalProfile } from "./customer-center/personal-profile";
import CustomerDataForm from "./customer-data-form";
import CustomerDataFormLogin from './customer-data-form-login';
import DateFormatter from "./date-formatter";
import DeliveryOptionComponent from './delivery-option';
import DeliveryRegionComponent from './delivery-region';
import { ResetChangePasswordForm } from "./forgot-password/reset-change-password-form";
import { ResetPasswordForm } from "./forgot-password/reset-password-form";
import ImageLoader from './image-loader';
import { InquiryForm } from "./inquiry-form";
import LanguageSelector from './language-selector';
import { LoginForm } from "./login-form";
import OrderDetailEdit from './order-detail-edit';
import OrderFilter from "./order-filter";
import PaginatorComponent from './paginator';
import PayPalPayment from './paypal-payment';
import PersonAddress from './person-address';
import PersonContact from './person-contact';
import PersonEdit from './person-edit';
import { getProductId, initProductDetail, loadProductVariant, ProductPageState } from './product-detail';
import ProductDetailDownload from './product-detail-download';
import ProductOrdering from './product-ordering';
import { ProductSampleInquiryForm } from './product-sample-inquiry-form';
import { RegisterForm } from "./register-form";
import SearchBar from './search-bar';
import SharedCart from './shared-cart';
import StringLocalizer from "./string-localizer";
import StripePayment from './stripe-payment';
import TronLogeEventLogger from './tron-logo-event-logger';
import ProductToggleCollection from './product-toggle-collection';
import VisualizationToggle from './visualization-toggle';
import TronLogoLinks from './tron-logo-links';
import MolliePayment from './mollie-payment';
import PayUPayment from './payU-payment';
import SaferPayPayment from './saferpay-payment';
import VatValidation from './vat-validation';
import ProductCount, { CartItemCustomData } from './product-count';
import EmergencyWarning from './emergency-warning';
import { CompleteRegistrationRequest } from './complete-registration/complete-registration-request';
import { CompleteRegistration } from './complete-registration/complete-registration';
import DiscountCoupons from './discount-coupons';
import DiscountCouponsList from './discount-coupons-list';
import FirstVisitorPopup from './first-visitor-popup';
import FirstVisitorInitializer from './first-visitor-initializer';
import RedsysPayment from './redsys-payment';
import initProductCollectionCarousel from './product-collection-carousel';
import './js/scripts.js';
import './js/site.js';
import initSimilarProductsCarousel from './similar-products-carousel';
import initSimilarProductsCarouselProad from './similar-products-carousel-proad';
import initLastVisitedProductsCarousel from './last-visited-products-carousel';
import initLastVisitedProductsCarouselProad from './last-visited-products-carousel-proad';
import Form from './form';
import initCampaignBannerCarousel from './campaign-banner-carousel';
import initVariantListCollapse from './variant-list-collapse';

export { getAllowedCookies, getCookie };
export { openTronDesignerStartDialog } from './tron-designer/start-dialog';
import { rememberTlUserGuid, initTronDesigner, initTronDesignerCartActions } from './tron-designer/tron-designer';
import { initCartDesignInfo } from './tron-designer/cart-design-info';
export { retryCartLogoThumb } from './tron-designer/cart-logo-gallery';

let filterBox: FilterBox;
let collapsable: Collapsable;
export let stringLocalizer: StringLocalizer;
const initActions: (() => void)[] = [];

export function initLanguageSelector(): void {
    const elements = document.querySelectorAll('[data-component="LanguageSelector"]');
    elements.forEach(e => {
        const selector = new LanguageSelector();
        selector.init(e);
    });
}

export function initCurrencySelector(): void {
    const elements = document.querySelectorAll('[data-component="CurrencySelector"]');
    elements.forEach(e => {
        const selector = new CurrencySelector();
        selector.init(e);
    });
}

export const initSearchBar = () => new SearchBar().init();

export async function initFilterBox(): Promise<void> {
    if (filterBox) {
        return;
    }

    filterBox = new FilterBox();
    await filterBox.init();
}

async function initFilter(
    filterName: string,
    filterFactoryMethod: (
        element: HTMLElement,
        filterBox: FilterBox
    ) => FilterBase
): Promise<void> {
    await initFilterBox();

    const filterElements = [
        ...document.querySelectorAll(`[data-component="${filterName}"]`)
    ];

    const filterValues = filterBox.getInitialFilterValues(filterName);

    filterElements.forEach(element => {
        const filter = filterFactoryMethod(element as HTMLElement, filterBox);
        filter.init();
        if (filterValues[filter.paramId]) {
            filter.setValues(filterValues[filter.paramId]);
        }
    });
}

function initColorFilter(): void {
    initFilter("ColorFilter", (e, b) => new ColorFilter(e, b));
}

function initTextileSizeFilter(): void {
    initFilter("TextileSizeFilter", (e, b) => new TextileSizeFilter(e, b));
}

function initCheckboxFilter(): void {
    initFilter("CheckboxFilter", (e, b) => new CheckboxFilter(e, b));
}

function initRangeFilter(): void {
    initFilter("RangeFilter", (e, b) => new RangeFilter(e, b));
}

function initCheckboxValueFilter(): void {
    initFilter("CheckboxValueFilter", (e, b) => new CheckboxValueFilter(e, b));
}

function initCheckboxFlatFilter(): void {
    initFilter("CheckboxFlatFilter", (e, b) => new CheckboxFlatFilter(e, b));
}

function initRadioItemsFilter(): void {
    initFilter("RadioItemsFilter", (e, b) => new RadioItemsFilter(e, b));
}

function initSelectFilter(): void {
    initFilter("SelectFilter", (e, b) => new SelectFilter(e, b));
}

function initTextFilter(): void {
    initFilter("TextFilter", (e, b) => new TextFilter(e, b));
}

export function initFilters() {
    initColorFilter();
    initTextileSizeFilter();
    initCheckboxFilter();
    initCheckboxFlatFilter();
    initRangeFilter();
    initCheckboxValueFilter();
    initRadioItemsFilter();
    initSelectFilter();
    initTextFilter();
}

export function initCustomerDataForm(): void {
    new CustomerDataForm(document.querySelector('#customer-form'));
}

export function initCustomerDataFormLogin(): void {
    new CustomerDataFormLogin('show-login');
}

export function initRegisterForm(): void {
    new RegisterForm(document.querySelector('#register-form'));
}

export function initCompleteRegistration(): void {
    new CompleteRegistration();
}

export function processRegisterForm(event: Event): void {
    const formElement = event.target as HTMLFormElement;
    const registerForm = new RegisterForm(formElement);

    registerForm.processForm(event);
}

export async function addProductToCart(productId: number, quantity: number, customData: CartItemCustomData = null): Promise<void> {
    const cart = new Cart();
    await cart.addProduct([new ProductCount(productId, null, quantity, customData)]);
}

export function processLoginForm(event: Event): void {
    const formElement = event.target as HTMLFormElement;
    const loginForm = new LoginForm(formElement);

    loginForm.processForm(event);
}

export function processLoginFormFromCart(event: Event): void {
    const formElement = event.target as HTMLFormElement;
    const loginForm = new CartLoginForm(formElement);

    loginForm.processForm(event);
}

export function processChangePasswordForm(event: Event): void {
    const formElement = event.target as HTMLFormElement;
    const changePasswordForm = new ChangePasswordForm(formElement);

    changePasswordForm.processForm(event);
}

export function initCart(): void {
    const cart = new Cart();
    const cartFileUploader = new CartFileUploader();
    cartFileUploader.init(cart, false);
    cart.init();
    cart.initForCartSharing("shareCart");
}

export function initSharedCart(): void {
    const sharedCart = new SharedCart();
    sharedCart.initForSharedCart("addItemsToCart");

    // Read-only TronDesigner display, same as the cart/order-detail: enrich the design rows with live
    // print-job facts + thumbnail (cart-design-info) and wire the proofreading download. No create/edit
    // actions exist in the shared-cart markup, so initTronDesignerCartActions only binds the download.
    initTronDesigner();
    initTronDesignerCartActions();
    initCartDesignInfo();
}

export function initOrderDetailEdit(): void {
    const orderDetailEdit = new OrderDetailEdit();
    orderDetailEdit.init();
}

export function initCategoryMenu(): void {
    const element = document.querySelector(
        '[data-component="CategoryMenu"]'
    ) as HTMLElement;

    if (!element) return;

    const menu = new CategoryMenu(element);
    menu.init();
}

export function initDeliveryOption(): void {
    const form = document.querySelector<HTMLElement>('[data-component="DeliveryOptions"]');
    if (!form || form.dataset.deliveryOptionBound === '1') {
        return;
    }
    form.dataset.deliveryOptionBound = '1';
    new DeliveryOptionComponent();
}

export function initDeliveryRegion(): void {
    const action = new ActionCreator().reloadComponents(['DeliveryOptions', 'VatValidation', 'CartSummary']);
    action.successAction = () => { initDeliveryOption(); new VatValidation().loadVatValidation() }
    new DeliveryRegionComponent((_) => new AjaxDispatcher().dispatchGetAction(action, true));
}

export async function initVatValidation(): Promise<void> {
    const vatValidation = new VatValidation();
    await vatValidation.loadVatValidationAndCartSummary();
}

export async function initDiscountCoupons(): Promise<void> {
   new DiscountCoupons();
}

export async function initDiscountCouponsList(): Promise<void> {
    new DiscountCouponsList();
}

export async function initFirstVisitorPopup(): Promise<void> {
    new FirstVisitorPopup();
}
export async function initFirstVisitorInitializer(): Promise<void> {
    new FirstVisitorInitializer();
}

export function initPaginator(): void {
    const paginators = document.querySelectorAll(
        '[data-component="Paginator"]'
    );
    paginators.forEach(element => {
        if (element.hasAttribute('data-initialized')) return;
        element.setAttribute('data-initialized', 'true');
        const paginator = new PaginatorComponent();
        paginator.init(element);
    });
}

export function initProductOrdering(): void {
    const orderings = document.querySelectorAll(
        '[data-component="ProductOrdering"]'
    );
    orderings.forEach(element => {
        const ordering = new ProductOrdering(element as HTMLElement);
        ordering.init();
    });
}

export function initProductList(): void {
    markComponentInitialized("initProductList");
    initProductOrdering();
    initVariantListCollapse();
}

export async function initProductDetailPage(): Promise<void> {
    await initProductDetail();
    window.history.replaceState(new ProductPageState(getProductId()), '', window.location.href);
    window.addEventListener("popstate", async evt => {
        await loadProductVariant(evt.state as ProductPageState)
    });
}

export function initProductDetailDownload(): void {
    const productDetailDownload = new ProductDetailDownload('download-product-sheet-btn', getProductId());
    productDetailDownload.init();
}

export function initCompanyAddress(): void {
    const companyAddress = new CompanyAddress();
    companyAddress.init();
}

export function initPersonAddress(): void {
    const personAddress = new PersonAddress();
    personAddress.init();
}

export function initCompanyPerson(): void {
    const companyPerson = new CompanyPerson();
    companyPerson.init();
}

export function initPersonContact(): void {
    const personContact = new PersonContact();
    personContact.init();
}

export function initCompanyContact(): void {
    const companyContact = new CompanyContact();
    companyContact.init();
}

export function initPersonEdit(): void {
    const personEdit = new PersonEdit();
    personEdit.init();
}

export function initCompanyEdit(): void {
    const companyEdit = new CompanyEdit();
    companyEdit.init();
}

export function initTronLogoLogger(): void {
    const tronLogoEventLogger = new TronLogeEventLogger();
    tronLogoEventLogger.init();
}

export function filterOrders(event: Event): void {
    const orderFilter = new OrderFilter();
    orderFilter.filter(event);
}

export function processOrderSorting(event: Event): void {
    const orderFilter = new OrderFilter();
    orderFilter.sort(event);
}

export function initCompanyProfile(): void {
    new CompanyProfile();
}

export function initPersonalProfile(): void {
    new PersonalProfile();
}

export async function openInquiryForm(event: Event): Promise<void> {
    const inquiryForm = new InquiryForm();
    await inquiryForm.openInPopup(event);
}

export async function openProductSampleInquiryForm(event: Event): Promise<void> {
    const inquiryForm = new ProductSampleInquiryForm(getProductId());
    await inquiryForm.openInPopup(event);
}

export function processResetPasswordForm(event: Event): void {
    const resetPasswordForm = new ResetPasswordForm();
    resetPasswordForm.process(event);
}

export function processChangePasswordResetForm(event: Event): void {
    const resetChangePasswordForm = new ResetChangePasswordForm();
    resetChangePasswordForm.process(event);
}

export function processCompleteRegistrationRequestForm(event: Event): void {
    const resetPasswordForm = new CompleteRegistrationRequest();
    resetPasswordForm.process(event);
}

export function processCompleteRegistrationForm(event: Event): void {
    const resetPasswordForm = new CompleteRegistration();
    resetPasswordForm.process(event);
}

export function processLogoutForm(event: Event): void {
    const formElement = document.querySelector('[data-component=LogoutForm]') as HTMLFormElement;
    const actionUrl = formElement.getAttribute('action');
    
    const form = new Form(actionUrl, formElement);
    form.process(event);
}

export function initDateFormatter(): void {
    new DateFormatter();
}

export function closePopup(event: Event): void {
    event.preventDefault();
    const popup = getPopup();
    popup.close();
}

export function openPopup(html: string) {
    var popup = getPopup();
    popup.openRawHtml(html);
}

export function initStringLocalizer(): void {
    stringLocalizer = new StringLocalizer();
    stringLocalizer.init();
}

export function initStripePayment(): void {
    new StripePayment();
} 

export function initRedsysPayment(): void {
    new RedsysPayment();
}

export function initMolliePayment(): void {
    new MolliePayment();
}

export function initPayUPayment(): void {
    new PayUPayment();
}

export function initSaferPayPayment(): void {
    new SaferPayPayment();
}
export async function initPayPalPayment(): Promise<void> {
    let paypal = new PayPalPayment();
    await paypal.initButton();
}

export function initContactForm(): void {
    new ContactForm().init();
}

export function initCookieConsent(): void {
    const cookieConsent = new CookieConsent();
    cookieConsent.init();
}

export function loadImages(): void {
    new ImageLoader().load();
}

export function addInitAction(action: () => void): void {
    initActions.push(action);
}

export function initVisualizationToggle(): void {
    markComponentInitialized("initVisualizationToggle");
    const visualizationToggle = new VisualizationToggle();
    visualizationToggle.init();
}

export function initProductToggleCollection(): void {
    const productToggleCollection = new ProductToggleCollection();
    productToggleCollection.init();
}

export function initTronLogoLinks(): void {
    const tronLogoLinks = new TronLogoLinks();
    tronLogoLinks.init();
}

export async function initEmergencyWarning(): Promise<void> {
    const emergencyWarning = new EmergencyWarning();
    await emergencyWarning.init();
}

function processInitActions() {
    initActions.forEach(x => x());
}

// Initialize on DOMContentLoaded, not window 'load' — 'load' waits for every image/iframe,
// which delayed all interactivity (cart buttons, filters, search) on slow connections.
// The bundle is at the end of <body>, so the DOM is usually already parsed when this runs.
function startComponents(): void {
    scanAndExecuteFunctions();
    processInitActions();
    addComponentsScriptsObserver();
    // Stash the TronLogo user guid whenever the TL engine is on the page (product detail, TCC) — the
    // cart uses the cached value to render TL logos on pages where the engine never loads.
    rememberTlUserGuid();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startComponents);
} else {
    startComponents();
}

const functionMap: { [key: string]: Function } = {
    'initProductCollectionCarousel': initProductCollectionCarousel,
    'initRedsysPayment': initRedsysPayment,
    'initStripePayment': initStripePayment,
    'initPayPalPayment': initPayPalPayment,
    'initMolliePayment': initMolliePayment,
    'initPayUPayment': initPayUPayment,
    'initCart': initCart,
    'initCategoryMenu': initCategoryMenu,
    'initCheckboxFilter': initCheckboxFilter,
    'initCheckboxFlatFilter': initCheckboxFlatFilter,
    'initCheckboxValueFilter': initCheckboxValueFilter,
    'initColorFilter': initColorFilter,
    'initCompanyAddress': initCompanyAddress,
    'initCompanyContact': initCompanyContact,
    'initCompanyEdit': initCompanyEdit,
    'initCompanyPerson': initCompanyPerson,
    'initCompanyProfile': initCompanyProfile,
    'initCompleteRegistration': initCompleteRegistration,
    'initContactForm': initContactForm,
    'initCookieConsent': initCookieConsent,
    'initCurrencySelector': initCurrencySelector,
    'initCustomerDataFormLogin': initCustomerDataFormLogin,
    'initCustomerDataForm': initCustomerDataForm,
    'initDateFormatter': initDateFormatter,
    'initStringLocalizer': initStringLocalizer,
    'initFirstVisitorInitializer': initFirstVisitorInitializer,
    'initDeliveryOption': initDeliveryOption,
    'initDeliveryRegion': initDeliveryRegion,
    'initDiscountCouponsList': initDiscountCouponsList,
    'initDiscountCoupons': initDiscountCoupons,
    'initEmergencyWarning': initEmergencyWarning,
    'initFilterBox': initFilterBox,
    'loadImages': loadImages,
    'initLanguageSelector': initLanguageSelector,
    'initOrderDetailEdit': initOrderDetailEdit,
    'initPaginator': initPaginator,
    'initRadioItemsFilter': initRadioItemsFilter,
    'initRangeFilter': initRangeFilter,
    'initSelectFilter': initSelectFilter,
    'initTextFilter':  initTextFilter,
    'initTextileSizeFilter': initTextileSizeFilter,
    'initPersonAddress': initPersonAddress,
    'initPersonContact': initPersonContact,
    'initPersonEdit':initPersonEdit,
    'initPersonalProfile': initPersonalProfile,
    'initProductDetailPage': initProductDetailPage,
    'initProductDetailDownload': initProductDetailDownload,
    'initProductList': initProductList,
    'initProductOrdering': initProductOrdering,
    'initProductToggleCollection': initProductToggleCollection,
    'initRegisterForm': initRegisterForm,
    'initSaferPayPayment': initSaferPayPayment,
    'initSearchBar': initSearchBar,
    'initSharedCart': initSharedCart,
    'initTronLogoLinks': initTronLogoLinks,
    'initTronLogoLogger': initTronLogoLogger,
    'initVatValidation': initVatValidation,
    'initVisualizationToggle': initVisualizationToggle,
    'initSimilarProductsCarousel': initSimilarProductsCarousel,
    'initSimilarProductsCarouselProad': initSimilarProductsCarouselProad,
    'initLastVisitedProductsCarousel': initLastVisitedProductsCarousel,
    'initLastVisitedProductsCarouselProad': initLastVisitedProductsCarouselProad,
    'initCampaignBannerCarousel': initCampaignBannerCarousel,
    'initVariantListCollapse': initVariantListCollapse
};

// Runs the data-ts-script init functions of one element, at most ONCE per element instance.
// Without the per-element guard the same element could be initialized twice (startup scan +
// MutationObserver, or repeated observer firings during AJAX swaps), attaching duplicate listeners.
// honorLegacyParentMarker: markComponentInitialized flags the PARENT of the script element (used
// by initProductList / initVisualizationToggle) and was historically checked ONLY by the observer —
// the startup scan must NOT honor it, or a marked parent would skip unrelated sibling components.
function runTsScripts(element: Element, honorLegacyParentMarker: boolean): void {
    if (element.getAttribute('data-ts-script-initialized') === 'true') {
        return;
    }
    if (honorLegacyParentMarker
        && element.parentElement
        && element.parentElement.getAttribute('data-ts-script-initialized') === 'true') {
        return;
    }

    const attValue = element.getAttribute('data-ts-script');
    if (!attValue) {
        return;
    }

    element.setAttribute('data-ts-script-initialized', 'true');

    parseTsScriptAttributeValue(attValue).forEach(functionName => {
        if (functionName && functionMap[functionName]) {
            functionMap[functionName]();
        } else {
            console.error(`Function ${functionName} not found`);
        }
    });
}

function scanAndExecuteFunctions(): void {
    document.querySelectorAll('[data-ts-script]').forEach(element => runTsScripts(element, false));
}

function parseTsScriptAttributeValue(value: string):string[]{
    if (value.includes(',')) {
        return value.split(',');
    }
    return [value];
}

function markComponentInitialized(scriptName: string):void{
    const elements = document.querySelectorAll<HTMLElement>(`[data-ts-script="${scriptName}"]`);
    elements.forEach(x => {
        x.parentElement.setAttribute("data-ts-script-initialized", "true");
    });
}

function addComponentsScriptsObserver():void{
    const config = {
        childList: true,
        subtree: true
    };

    const observer = new MutationObserver(handleMutations);
    observer.observe(document.body, config);
}

function handleMutations(mutations: MutationRecord[], observer: MutationObserver): void {
    mutations.forEach(mutation => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            mutation.addedNodes.forEach(node => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                    // Intentionally only the inserted node itself (not descendants) — AJAX flows
                    // init nested components explicitly via their successAction callbacks, and
                    // scanning descendants here would double-initialize them (e.g. slick carousels).
                    runTsScripts(node as Element, true);
                }
            });
        }
    });
}
